/**
 * InstallPromptCard — Advoset-branded install banner, per-app.
 *
 * The card is rendered globally in App.js but internally resolves the
 * currently-active PWA (user | lawyer | admin) via `getActiveApp()` and
 * hydrates its own copy + colours + monogram from the registry
 * projection. Also runs the C6 install-time role gate before firing
 * `beforeinstallprompt` so people don't end up with a home-screen icon
 * for an app their session can't sign into.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Download, X, Rocket, HomeIcon, Zap, ShieldAlert } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { trackInstallEvent } from "@/lib/pwaAnalytics";
import { evaluateInstallGate, getActiveApp } from "@/lib/pwaAppConfig";
import { useAuth } from "@/context/AuthContext";

const APPEAR_DELAY_MS = 4000; // give the page ~4s to settle first

export default function InstallPromptCard() {
  const { canInstall, promptInstall, dismiss } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};

  // Resolve once on mount — the resolver script has already run by then.
  const app = useMemo(() => getActiveApp(), []);
  const gate = useMemo(() => evaluateInstallGate(app, user), [app, user]);

  useEffect(() => {
    if (!canInstall) { setVisible(false); return undefined; }
    // C6: honour the install-time role gate. Blocked gates → never show.
    if (gate === "restricted") { setVisible(false); return undefined; }
    const t = setTimeout(() => {
      setVisible(true);
      trackInstallEvent("install_prompt_shown", "web_prompt");
    }, APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, [canInstall, gate]);

  if (!canInstall || !visible || !app) return null;

  const onInstall = async () => {
    setBusy(true);
    try { await promptInstall(); }
    finally { setBusy(false); setVisible(false); }
  };
  const onLater = () => { dismiss(); setVisible(false); };

  // Registry-driven visuals.
  const palette = app.branding.primary_palette || {};
  const bg = palette.primary || "#12355B";
  const accent = palette.accent || "#D4AF37";
  const surface = palette.surface || "#FFFFFF";
  const displayName = app.identity.display_name;
  const monogram = displayName?.[0] || "A";
  const iconSrc = app.branding.favicon_url || app.branding.apple_touch_icon;

  // C6: warning banner when signed-in role doesn't match the active app.
  const warning = gate === "role_mismatch"
    ? `You're signed in as ${user?.role}. Installing ${displayName} won't carry over your session.`
    : (gate === "invite_only"
        ? `${displayName} is invite-only — you'll still need approval to sign in.`
        : null);

  return (
    <div
      className="fixed left-4 right-4 bottom-4 z-[60] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt"
      data-app-slug={app.identity.id}
      role="dialog"
      aria-labelledby="install-prompt-title"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className="rounded-2xl shadow-2xl ring-1 overflow-hidden"
        style={{ backgroundColor: bg, color: surface, borderColor: `${surface}22` }}
      >
        <div className="flex items-start gap-3 p-4">
          <div
            className="shrink-0 w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center font-black text-lg"
            style={{ backgroundColor: accent, color: bg, fontFamily: "Poppins" }}
            data-testid="install-prompt-icon"
          >
            {iconSrc ? (
              <img src={iconSrc} alt="" className="w-full h-full object-cover" />
            ) : (
              monogram
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3
                id="install-prompt-title"
                className="text-base font-extrabold"
                style={{ fontFamily: "Poppins" }}
                data-testid="install-prompt-title"
              >
                Install {displayName}
              </h3>
              <button
                onClick={onLater}
                data-testid="install-prompt-close"
                aria-label="Dismiss"
                className="opacity-70 hover:opacity-100 -mr-1 -mt-1 p-1"
                style={{ color: surface }}
              >
                <X size={16} />
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-[12px]" style={{ color: `${surface}CC` }}>
              <li className="inline-flex items-center gap-1.5"><HomeIcon size={11} style={{ color: accent }} /> Faster access from your Home Screen</li>
              <li className="inline-flex items-center gap-1.5"><Rocket size={11} style={{ color: accent }} /> App-like experience</li>
              <li className="inline-flex items-center gap-1.5"><Zap size={11} style={{ color: accent }} /> Faster loading</li>
            </ul>

            {warning && (
              <div
                className="mt-2 flex items-start gap-1.5 text-[11px] rounded-md px-2 py-1.5"
                data-testid="install-prompt-warning"
                style={{ backgroundColor: `${surface}12`, color: `${surface}CC` }}
              >
                <ShieldAlert size={11} className="mt-[2px]" style={{ color: accent }} />
                <span>{warning}</span>
              </div>
            )}

            <div className="mt-3 flex gap-2">
              <button
                onClick={onInstall}
                disabled={busy}
                data-testid="install-prompt-install"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl text-sm font-extrabold py-2.5 transition disabled:opacity-60"
                style={{ backgroundColor: accent, color: bg }}
              >
                <Download size={14} /> Install App
              </button>
              <button
                onClick={onLater}
                data-testid="install-prompt-later"
                className="rounded-xl border text-sm font-bold px-3 py-2.5 transition"
                style={{ borderColor: `${surface}26`, color: `${surface}D9` }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
